import type { IComponentController, IFilterService, IScope } from "angular";
import type { Duration, Moment } from "moment-timezone";
import { duration, utc } from "moment-timezone";

import { cloneDeep, find, first, head, isEqual, isNil, last } from "lodash";
import type { IResultDaySchedule, ISchedule } from "App/Modules/Models";
import { DayType, ScheduleTypes } from "App/Modules/Models";

const scheduleColors = {
    holidaySchedule: {
        colors: ["#C390EA", "rgba(195,144,234,0.25)"],
    },
    scheduleExceptions: {
        colors: ["#EC6065", "#F9C8C9"],
    },
    temporaryWeeklySchedule: {
        colors: ["#ea7a00", "rgba(133,212,250,0.3)"],
    },
    weeklySchedule: {
        colors: ["#ACFF95", "rgba(172,255,149,0.3)"],
    },
};

export default class SchemeViewComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "$filter"];

    public colorTheme: string;

    public dayTypes: DayType[];

    public lowResolutionMode: boolean;

    public timeZone: string;

    public schedule: ISchedule;

    public initialDate: Moment;

    public previousInitialDate: Moment;

    public previousSchedule: ISchedule;

    public resultSchedule: IResultDaySchedule[][];

    public durationFilter: (duration: Duration) => string = this.$filter("mmDurationToLocalTimeString");

    constructor(private readonly $scope: IScope, private readonly $filter: IFilterService) {}

    public $onInit() {
        this.previousSchedule = cloneDeep(this.schedule);
        this.previousInitialDate = this.initialDate.clone();
        this.resultSchedule = this.getMonthResultSchedule();
    }

    public $doCheck() {
        if (
            (this.schedule && this.previousSchedule && !isEqual(this.schedule, this.previousSchedule)) ||
            (this.initialDate && this.previousInitialDate && !this.initialDate.isSame(this.previousInitialDate))
        ) {
            this.previousSchedule = cloneDeep(this.schedule);
            this.previousInitialDate = this.initialDate.clone();
            this.resultSchedule = this.getMonthResultSchedule();
        }
    }

    public getMonthResultSchedule() {
        const monthResultSchedule = [];
        const currentDateWithoutTZ = utc(utc().tz(this.timeZone).format("YYYY-MM-DD"));
        let firstDay = this.initialDate.isSame(currentDateWithoutTZ, "month")
            ? currentDateWithoutTZ.startOf("day")
            : this.initialDate.clone();

        for (
            let lastDay = firstDay.clone().endOf("week").startOf("day");
            lastDay.isBefore(firstDay.clone().endOf("month"));
            lastDay = firstDay.clone().endOf("week").startOf("day")
        ) {
            monthResultSchedule.push(this.getWeekResultSchedule(firstDay, lastDay));
            firstDay = lastDay.clone().add(1, "days");
        }

        monthResultSchedule.push(this.getWeekResultSchedule(firstDay, firstDay.clone().endOf("month")));

        return cloneDeep(monthResultSchedule);
    }

    public getWeekResultSchedule(firstDay: Moment, lastDay: Moment) {
        const resultSchedule = [];

        for (
            let currentDay = firstDay.clone();
            currentDay.isSameOrBefore(lastDay, "d");
            currentDay = currentDay.clone().add(1, "d")
        ) {
            resultSchedule.push({
                baseSchedule: this.getBaseSchedule(currentDay),
                date: currentDay,
                dayType: this.schedule.weeklySchedule.daySchedules[currentDay.isoWeekday() - 1].dayType,
                holidaySchedule: this.getActiveHolidayScheduleConfig(currentDay),
                scheduleExceptions: this.getScheduleExceptions(currentDay),
            });
        }

        return resultSchedule;
    }

    public getBaseSchedule(date: Moment) {
        let baseSchedule = this.getActiveTemporaryScheduleConfig(date);

        if (isNil(baseSchedule)) {
            baseSchedule = this.getWeeklyScheduleConfig(date);
        }

        return baseSchedule;
    }

    public getActiveHolidayScheduleConfig(date: Moment) {
        const startOfDate = date.clone().startOf("day");
        if (this.schedule && this.schedule.holidaySchedule && this.schedule.holidayConfigurations) {
            const scheduleForDate = this.schedule.holidaySchedule.holidays.find((holiday) =>
                startOfDate.isBetween(
                    holiday.dtStart.clone().subtract(1, "d"),
                    holiday.dtStop.clone().add(1, "d"),
                    null,
                    "[]",
                ),
            );

            if (scheduleForDate) {
                const { daySchedules } = head(this.schedule.holidayConfigurations);
                let timePeriods;

                if (startOfDate.isBetween(scheduleForDate.dtStart, scheduleForDate.dtStop, null, "[]")) {
                    const dayOnHoliday = find(daySchedules, (schedule) => schedule.dayType === DayType.DayOnHoliday);
                    timePeriods = dayOnHoliday.scheduleTimePeriods;
                } else if (
                    startOfDate.isBetween(
                        scheduleForDate.dtStart.clone().subtract(1, "day"),
                        scheduleForDate.dtStart,
                        null,
                        "[)",
                    )
                ) {
                    const dayBeforeHoliday = find(
                        daySchedules,
                        (schedule) => schedule.dayType === DayType.DayBeforeHoliday,
                    );
                    timePeriods = dayBeforeHoliday.scheduleTimePeriods;
                } else if (
                    startOfDate.isBetween(
                        scheduleForDate.dtStop.clone(),
                        scheduleForDate.dtStop.clone().add(1, "day"),
                        null,
                        "[]",
                    )
                ) {
                    const dayAfterHoliday = find(
                        daySchedules,
                        (schedule) => schedule.dayType === DayType.DayAfterHoliday,
                    );
                    timePeriods = dayAfterHoliday.scheduleTimePeriods;
                }

                return {
                    color: scheduleColors.holidaySchedule.colors[0],
                    timePeriods,
                    type: ScheduleTypes.HolidaySchedule,
                };
            }
        }
        return undefined;
    }

    public getActiveTemporaryScheduleConfig(date: Moment) {
        if (
            !this.schedule ||
            !this.schedule.temporaryWeeklySchedule ||
            !this.schedule.temporaryWeeklySchedule.datePeriods
        ) {
            return undefined;
        }

        const scheduleForDate = this.schedule.temporaryWeeklySchedule.datePeriods.find((period) =>
            date.isBetween(period.dateStart, period.dateStop, null, "[]"),
        );

        if (!scheduleForDate || !scheduleForDate.dateStart || !scheduleForDate.dateStop) {
            return undefined;
        }

        return {
            color: scheduleColors.temporaryWeeklySchedule.colors[0],
            timePeriods: this.schedule.temporaryWeeklySchedule.daySchedules[date.isoWeekday() - 1].scheduleTimePeriods,
            type: ScheduleTypes.TemporarySchedule,
        };
    }

    public getWeeklyScheduleConfig(date: Moment) {
        if (this.schedule && this.schedule.weeklySchedule) {
            return {
                color: scheduleColors.weeklySchedule.colors[0],
                timePeriods: this.schedule.weeklySchedule.daySchedules[date.isoWeekday() - 1].scheduleTimePeriods,
                type: ScheduleTypes.WeeklySchedule,
            };
        }
        return undefined;
    }

    public getScheduleExceptions(date: Moment) {
        if (!this.schedule || !this.schedule.scheduleExceptions) {
            return undefined;
        }

        const scheduleExceptionsForDate = this.schedule.scheduleExceptions.filter((scheduleException) => {
            if (!scheduleException.dtStart || !scheduleException.dtStop) {
                return undefined;
            }

            return date.isBetween(
                utc(scheduleException.dtStart.clone().tz(this.timeZone).format()),
                utc(scheduleException.dtStop.clone().tz(this.timeZone).subtract(1, "s").format("YYYY-MM-DD hh:mm:ss")),
                "d",
                "[]",
            );
        });

        return scheduleExceptionsForDate.map((scheduleException) => {
            const start = this.fromMomentToDurationMinutes(scheduleException.dtStart, date, this.timeZone);
            const stop = this.fromMomentToDurationMinutes(
                scheduleException.dtStop.clone().subtract(1, "s"),
                date,
                this.timeZone,
            );

            return {
                color: scheduleException.isOnType
                    ? scheduleColors.scheduleExceptions.colors[0]
                    : scheduleColors.scheduleExceptions.colors[1],
                isOnType: scheduleException.isOnType,
                start,
                stop,
            };
        });
    }

    public fromMomentToDurationMinutes(momentToTranslate: Moment, date: Moment, timeZone: string) {
        const toTranslate = momentToTranslate.clone().tz(timeZone);

        const startOfDay = date.clone().tz(timeZone).startOf("day");

        if (!startOfDay.isDST() && toTranslate.isDST()) {
            toTranslate.add(1, "h");
        }

        let diff = toTranslate.diff(startOfDay, "m");
        diff = diff < 0 ? 0 : diff;
        diff = diff >= 24 * 60 ? 24 * 60 - 1 : diff;

        const dur = duration(diff, "m");

        return dur;
    }

    public getLocaleWeekDay(day: Moment, miniFormat: boolean) {
        return miniFormat ? day.format("dd").slice(0, 1) : day.format("dddd");
    }

    public isToday(date: Moment): boolean {
        return utc().tz(this.timeZone).isSame(date, "d");
    }

    public nextMonth() {
        this.initialDate.add(1, "month").startOf("month");
    }

    public previousMonth() {
        this.initialDate.subtract(1, "month").startOf("month");
    }

    public isPreviousMonthButtonDisabled() {
        return this.initialDate.isSame(utc().tz(this.timeZone), "month");
    }

    public isNextMonthButtonDisabled() {
        return this.initialDate.isSame(utc().tz(this.timeZone).clone().add(1, "year"), "month");
    }

    public getDaysOfWeek(resultWeekSchedule: IResultDaySchedule[]) {
        const from = first(resultWeekSchedule).date.format("DD");
        const to = last(resultWeekSchedule).date.format("DD");
        const month = first(resultWeekSchedule).date.format("MMMM");

        return from === to ? `${from} ${month}` : `${from} - ${to} ${month}`;
    }
}
